"use client";

import { type RefObject, useRef, useState } from "react";
import {
  Checkbox,
  ErrorNotice,
  Field,
  SelectField,
  TextField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_PRIMARY,
  BUTTON_SECONDARY,
  BUTTON_SMALL,
  HINT,
} from "@/components/tool-kit/styles";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import {
  buildRuledSheet,
  type LengthUnit,
  MM_PER_INCH,
  type Orientation,
  type PaperSize,
  RULED_SPACING_MM,
  type RuledDrawing,
  type RuledInputs,
  type Sheet,
  sheetFor,
  truePrintCss,
} from "@/lib/tool-kit/classroom/paper";
import { downloadPng, downloadSvg } from "@/lib/tool-kit/download";

const SHEET_CLASS = "lp-sheet";
const WEIGHTS = { fine: 0.15, medium: 0.25, bold: 0.4 } as const;
const PRESETS: Record<LengthUnit, string[]> = {
  in: ["1", "3/4", "5/8", "1/2"],
  mm: ["25", "20", "16", "12"],
};
const DEFAULTS: Record<
  LengthUnit,
  {
    top: string;
    side: string;
    marginAt: string;
    custom: string;
    primary: string;
  }
> = {
  in: {
    top: "1",
    side: "1/4",
    marginAt: "1 1/4",
    custom: "3/8",
    primary: "3/4",
  },
  mm: { top: "25", side: "6", marginAt: "32", custom: "8", primary: "20" },
};

/** English strings. A translated content file overrides these through `ui`. */
export const LINED_PAPER_GENERATOR_STRINGS = {
  rulingLabel: "Ruling",
  rulings: {
    wide: "Wide ruled — 11/32 in (8.7 mm)",
    college: "College ruled — 9/32 in (7.1 mm)",
    narrow: "Narrow ruled — 1/4 in (6.4 mm)",
    primary: "Primary — handwriting lines with a dashed midline",
    custom: "Custom spacing",
  },
  paperLabel: "Paper size",
  papers: { letter: "US Letter (8.5 × 11 in)", a4: "A4 (210 × 297 mm)" },
  orientationLabel: "Orientation",
  portrait: "Portrait",
  landscape: "Landscape",
  unitLabel: "Units",
  units: { in: "Inches", mm: "Millimeters" },
  unitShort: { in: "in", mm: "mm" },
  customLabel: "Line spacing",
  primaryLabel: "Writing height, top line to baseline",
  primaryHint:
    "The dashed midline sits halfway; half the writing height is left clear between rows for descenders.",
  topLabel: "Top margin",
  sideLabel: "Side and bottom margins",
  marginLine: "Vertical margin line",
  marginAtLabel: "Margin line from the left edge",
  nameLine: "Name and date line at the top",
  weightLabel: "Line weight",
  weights: { fine: "Fine", medium: "Medium", bold: "Bold" },
  lineColorLabel: "Line color",
  marginColorLabel: "Margin line color",
  name: "Name",
  date: "Date",
  print: "Print",
  downloadSvg: "Download SVG",
  downloadPng: "Download PNG (300 dpi)",
  lineCount: "one:{count} line per page|other:{count} lines per page",
  rowCount:
    "one:{count} writing row per page|other:{count} writing rows per page",
  spacingSummary: "{spacing} {unit} apart ({mm} mm).",
  trueScale:
    "Prints at true size: in the print dialog choose the same paper, set Scale to 100% (Actual size), and turn off Fit to page and Headers and footers.",
  previewLabel: "Lined paper preview, {ruling}",
  badSpacing:
    "Use a spacing from {min} to {max} mm (about 1/8 to 1 1/2 in). A primary writing height can be up to twice that.",
  badMargins: "Those margins leave no room for lines. Enter smaller margins.",
} as const;

type Strings = typeof LINED_PAPER_GENERATOR_STRINGS;
type Weight = keyof typeof WEIGHTS;
type Format = (value: number, options?: Intl.NumberFormatOptions) => string;
type Built = ReturnType<typeof buildRuledSheet>;

interface Settings extends RuledInputs {
  paper: PaperSize;
  orientation: Orientation;
  weight: Weight;
  lineColor: string;
  marginColor: string;
}

function PaperSvg({
  result,
  settings,
  sheet,
  strings,
  label,
  svgRef,
}: {
  result: Built & { drawing: RuledDrawing };
  settings: Settings;
  sheet: Sheet;
  strings: Strings;
  label: string;
  svgRef: RefObject<SVGSVGElement | null>;
}) {
  const { drawing } = result;
  const stroke = WEIGHTS[settings.weight];
  const headerY = result.top - 7;
  return (
    <svg
      aria-label={label}
      className={`${SHEET_CLASS} mx-auto block h-auto w-full max-w-xl rounded-sm border border-border bg-white shadow-sm`}
      height={sheet.cssHeight}
      preserveAspectRatio="xMidYMin slice"
      ref={svgRef}
      role="img"
      viewBox={`0 0 ${sheet.width} ${sheet.height}`}
      width={sheet.cssWidth}
      xmlns="http://www.w3.org/2000/svg"
    >
      <rect fill="#ffffff" height={sheet.height} width={sheet.width} />
      {drawing.lines.map((line) => (
        <line
          key={`${line.kind}-${line.y}`}
          stroke={settings.lineColor}
          strokeDasharray={line.kind === "mid" ? "2 1.5" : undefined}
          strokeWidth={line.kind === "mid" ? stroke * 0.8 : stroke}
          x1={drawing.x1}
          x2={drawing.x2}
          y1={line.y}
          y2={line.y}
        />
      ))}
      {drawing.marginLine ? (
        <line
          stroke={settings.marginColor}
          strokeWidth={stroke * 1.4}
          x1={drawing.marginLine.x}
          x2={drawing.marginLine.x}
          y1={drawing.marginLine.y1}
          y2={drawing.marginLine.y2}
        />
      ) : null}
      {settings.nameLine ? (
        <g
          fill="#333333"
          fontFamily="Helvetica, Arial, sans-serif"
          fontSize={4}
        >
          <text x={drawing.x1} y={headerY}>
            {strings.name}
          </text>
          <line
            stroke="#333333"
            strokeWidth={0.25}
            x1={drawing.x1 + 16}
            x2={drawing.x1 + (drawing.x2 - drawing.x1) * 0.6}
            y1={headerY + 0.8}
            y2={headerY + 0.8}
          />
          <text x={drawing.x2 - 52} y={headerY}>
            {strings.date}
          </text>
          <line
            stroke="#333333"
            strokeWidth={0.25}
            x1={drawing.x2 - 40}
            x2={drawing.x2}
            y1={headerY + 0.8}
            y2={headerY + 0.8}
          />
        </g>
      ) : null}
    </svg>
  );
}

function summaryFor(
  result: Built,
  settings: Settings,
  strings: Strings,
  locale: string,
  fmt: Format
): string {
  if (!result.drawing) {
    return "";
  }
  const rows = result.drawing.rows;
  const count =
    settings.ruling === "primary"
      ? tPlural(strings.rowCount, locale, rows, { count: fmt(rows) })
      : tPlural(strings.lineCount, locale, rows, { count: fmt(rows) });
  const inUnit =
    settings.unit === "in" ? result.spacing / MM_PER_INCH : result.spacing;
  return `${count} · ${t(strings.spacingSummary, {
    spacing: fmt(inUnit, { maximumFractionDigits: 3 }),
    unit: strings.unitShort[settings.unit],
    mm: fmt(result.spacing, { maximumFractionDigits: 1 }),
  })}`;
}

export default function LinedPaperGeneratorWidget() {
  const strings = useToolUi(LINED_PAPER_GENERATOR_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const svgRef = useRef<SVGSVGElement>(null);
  const [settings, setSettings] = useState<Settings>({
    ruling: "college",
    paper: "letter",
    orientation: "portrait",
    unit: "in",
    ...DEFAULTS.in,
    marginLine: true,
    nameLine: false,
    weight: "fine",
    lineColor: "#8fb4e3",
    marginColor: "#e06666",
  });
  const update = (patch: Partial<Settings>) =>
    setSettings((previous) => ({ ...previous, ...patch }));

  const sheet = sheetFor(settings.paper, settings.orientation);
  const result = buildRuledSheet(sheet, settings);
  const unit = strings.unitShort[settings.unit];
  const fileName = `lined-paper-${settings.ruling}-${settings.paper}`;

  function save(format: "svg" | "png") {
    const svg = svgRef.current;
    if (!svg) {
      return;
    }
    if (format === "svg") {
      downloadSvg(`${fileName}.svg`, svg);
      return;
    }
    downloadPng(`${fileName}.png`, svg, { scale: 300 / MM_PER_INCH });
  }

  const lengthField = (
    key: "custom" | "primary" | "top" | "side" | "marginAt",
    label: string,
    hint?: string
  ) => (
    <TextField
      hint={hint}
      id={`lp-${key}`}
      label={`${label} (${unit})`}
      numeric
      onChange={(value) => update({ [key]: value })}
      value={settings[key]}
    />
  );

  return (
    <div className="space-y-6 print:m-0 print:space-y-0">
      <style>{truePrintCss(`.${SHEET_CLASS}`, sheet)}</style>
      <div className="space-y-4 print:hidden">
        <SelectField
          id="lp-ruling"
          label={strings.rulingLabel}
          onChange={(ruling) => update({ ruling })}
          options={(
            ["wide", "college", "narrow", "primary", "custom"] as const
          ).map((value) => ({ value, label: strings.rulings[value] }))}
          value={settings.ruling}
        />
        <div className="grid gap-4 sm:grid-cols-3">
          <SelectField
            id="lp-paper"
            label={strings.paperLabel}
            onChange={(paper) => update({ paper })}
            options={(["letter", "a4"] as const).map((value) => ({
              value,
              label: strings.papers[value],
            }))}
            value={settings.paper}
          />
          <SelectField
            id="lp-orientation"
            label={strings.orientationLabel}
            onChange={(orientation) => update({ orientation })}
            options={[
              { value: "portrait", label: strings.portrait },
              { value: "landscape", label: strings.landscape },
            ]}
            value={settings.orientation}
          />
          <SelectField
            id="lp-unit"
            label={strings.unitLabel}
            onChange={(next) => update({ unit: next, ...DEFAULTS[next] })}
            options={(["in", "mm"] as const).map((value) => ({
              value,
              label: strings.units[value],
            }))}
            value={settings.unit}
          />
        </div>
        {settings.ruling === "custom"
          ? lengthField("custom", strings.customLabel)
          : null}
        {settings.ruling === "primary" ? (
          <div className="space-y-2">
            {lengthField("primary", strings.primaryLabel, strings.primaryHint)}
            <div className="flex flex-wrap gap-2">
              {PRESETS[settings.unit].map((preset) => (
                <button
                  aria-pressed={settings.primary === preset}
                  className={BUTTON_SMALL}
                  key={preset}
                  onClick={() => update({ primary: preset })}
                  type="button"
                >
                  {preset} {unit}
                </button>
              ))}
            </div>
          </div>
        ) : null}
        <div className="grid gap-4 sm:grid-cols-3">
          {lengthField("top", strings.topLabel)}
          {lengthField("side", strings.sideLabel)}
          {settings.marginLine
            ? lengthField("marginAt", strings.marginAtLabel)
            : null}
        </div>
        <div className="flex flex-wrap gap-x-6 gap-y-2">
          <Checkbox
            checked={settings.marginLine}
            label={strings.marginLine}
            onChange={(marginLine) => update({ marginLine })}
          />
          <Checkbox
            checked={settings.nameLine}
            label={strings.nameLine}
            onChange={(nameLine) => update({ nameLine })}
          />
        </div>
        <div className="grid gap-4 sm:grid-cols-3">
          <SelectField
            id="lp-weight"
            label={strings.weightLabel}
            onChange={(weight) => update({ weight })}
            options={(["fine", "medium", "bold"] as const).map((value) => ({
              value,
              label: strings.weights[value],
            }))}
            value={settings.weight}
          />
          <Field id="lp-line-color" label={strings.lineColorLabel}>
            <input
              className="h-10 w-full cursor-pointer rounded-lg border border-border bg-card p-1"
              id="lp-line-color"
              onChange={(event) => update({ lineColor: event.target.value })}
              type="color"
              value={settings.lineColor}
            />
          </Field>
          <Field id="lp-margin-color" label={strings.marginColorLabel}>
            <input
              className="h-10 w-full cursor-pointer rounded-lg border border-border bg-card p-1"
              id="lp-margin-color"
              onChange={(event) => update({ marginColor: event.target.value })}
              type="color"
              value={settings.marginColor}
            />
          </Field>
        </div>
        {result.error === "spacing" ? (
          <ErrorNotice>
            {t(strings.badSpacing, {
              min: RULED_SPACING_MM.min,
              max: RULED_SPACING_MM.max,
            })}
          </ErrorNotice>
        ) : null}
        {result.error === "margins" ? (
          <ErrorNotice>{strings.badMargins}</ErrorNotice>
        ) : null}
        <div className="flex flex-wrap gap-3">
          <button
            className={BUTTON_PRIMARY}
            disabled={!result.drawing}
            onClick={() => window.print()}
            type="button"
          >
            {strings.print}
          </button>
          <button
            className={BUTTON_SECONDARY}
            disabled={!result.drawing}
            onClick={() => save("svg")}
            type="button"
          >
            {strings.downloadSvg}
          </button>
          <button
            className={BUTTON_SECONDARY}
            disabled={!result.drawing}
            onClick={() => save("png")}
            type="button"
          >
            {strings.downloadPng}
          </button>
        </div>
        <p aria-live="polite" className={HINT}>
          {summaryFor(result, settings, strings, locale, fmt)}
        </p>
        <p className={HINT}>{strings.trueScale}</p>
      </div>
      {result.drawing ? (
        <PaperSvg
          label={t(strings.previewLabel, {
            ruling: strings.rulings[settings.ruling],
          })}
          result={result as Built & { drawing: RuledDrawing }}
          settings={settings}
          sheet={sheet}
          strings={strings}
          svgRef={svgRef}
        />
      ) : null}
    </div>
  );
}
